/**
 * Cichy auto-update PWA bez Service Workera.
 *
 * Mechanizm:
 *   1. Vite plugin w build emituje `dist/version.json` z `hash` (timestamp).
 *   2. Ten sam hash trafia do bundla jako `__BUILD_HASH__`.
 *   3. Po starcie aplikacji + przy każdym wybudzeniu z backgroundu fetchujemy
 *      `/version.json` z `cache: no-store` i porównujemy hash.
 *   4. Jeśli różny → cleanup cache + `location.reload()`.
 *
 * Dlaczego tak, a nie SW?
 * iOS Safari w trybie home-screen PWA ma własny capricieux cache shellu.
 * Standardowe SW + skipWaiting nie zawsze działa cleanly. Brutalny reload
 * po wykryciu nowej wersji jest deterministyczny i bez popupów.
 */

let inFlight: Promise<void> | null = null;
let lastCheckMs = 0;

const MIN_INTERVAL_MS = 30_000; // throttle: nie więcej niż raz na 30s

async function doCheck(): Promise<void> {
  const now = Date.now();
  if (now - lastCheckMs < MIN_INTERVAL_MS) return;
  lastCheckMs = now;

  try {
    const res = await fetch(`/version.json?t=${now}`, { cache: "no-store" });
    if (!res.ok) return;
    const data = (await res.json()) as { hash?: string };
    if (!data.hash) return;
    if (data.hash === __BUILD_HASH__) return;

    // Mamy nową wersję — wyczyść Cache Storage żeby Safari nie podsunęło
    // starego HTML/JS przy reloadzie, i przeładuj.
    if ("caches" in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch {
        /* ignore */
      }
    }
    // Małe opóźnienie żeby caches.delete miał czas na flush
    setTimeout(() => location.reload(), 50);
  } catch {
    // brak sieci / 404 → nic
  }
}

function scheduleCheck() {
  if (inFlight) return;
  inFlight = doCheck().finally(() => {
    inFlight = null;
  });
}

export function installAutoUpdate() {
  if (typeof window === "undefined") return;

  // Dev: hash to "Date.now() z dev startu", version.json może nie istnieć — zostaw.
  if (import.meta.env.DEV) return;

  // Pierwszy check po starcie — defer, żeby nie blokować pierwszego renderu
  if ("requestIdleCallback" in window) {
    (window as Window & {
      requestIdleCallback: (cb: () => void) => void;
    }).requestIdleCallback(scheduleCheck);
  } else {
    setTimeout(scheduleCheck, 1500);
  }

  // Wybudzenie z backgroundu (PWA wraca z app switchera, taba w Safari)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleCheck();
  });

  // Bonus: focus okna (desktop, Alt-Tab)
  window.addEventListener("focus", scheduleCheck);
}
